import { Box, MantineSpacing, StyleProp, Text, Tooltip } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { IconCloudUpload } from '@tabler/icons-react';
import React from 'react';
import classes from './DocumentsDropzone.module.css';
import {
  MAX_UPLOAD_SIZE_BYTES,
  UploadRejection,
  uploadRejectionReason,
  uploadRejectionsMessage,
  uploadRejectionsTitle,
  useAllowedFileTypes,
} from '../allowedFileTypes';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type DocumentsDropzoneShellProps = {
  onFilesSelected: (files: File[]) => void;
  testId: string;
  padding?: StyleProp<MantineSpacing>;
  activateOnClick?: boolean;
  openRef?: React.Ref<() => void | undefined>;
  children: React.ReactNode;
};

export default function DocumentsDropzoneShell({
  onFilesSelected,
  testId,
  padding = 'md',
  activateOnClick = true,
  openRef,
  children,
}: DocumentsDropzoneShellProps) {
  const { dropzoneAccept, extensions, formatsLabel } = useAllowedFileTypes();

  // Everything Mantine collected, accepted or not: its accept list matches on MIME only, which
  // rejects files the backend takes. uploadRejectionReason has the final say on both checks.
  const handleDroppedFiles = (accepted: File[], rejected: { file: File }[]) => {
    const rejections: UploadRejection[] = [];
    const files = [...accepted, ...rejected.map(rejection => rejection.file)].filter(file => {
      const reason = uploadRejectionReason(file, extensions);
      if (reason) rejections.push({ filename: file.name, reason });
      return reason === null;
    });

    if (rejections.length) {
      notifications.show({
        title: uploadRejectionsTitle(rejections),
        message: uploadRejectionsMessage(rejections, formatsLabel),
        color: 'red',
      });
    }
    if (files.length) onFilesSelected(files);
  };

  return (
    <Dropzone
      // onDropAny takes the whole drop. Mantine's type still requires onDrop, but using it would
      // split a mixed drop across onDrop and onReject, and so into two notifications.
      onDrop={() => {}}
      onDropAny={handleDroppedFiles}
      maxSize={MAX_UPLOAD_SIZE_BYTES}
      p={padding}
      activateOnClick={activateOnClick}
      openRef={openRef}
      accept={dropzoneAccept}
      className={activateOnClick ? `${classes.root} ${classes.clickable}` : classes.root}
      data-testid={testId}
    >
      {children}
    </Dropzone>
  );
}

export function DropzoneIcon({ large = false }: { large?: boolean }) {
  return (
    <Box className={large ? `${classes.iconTile} ${classes.iconTileLarge}` : classes.iconTile}>
      <IconCloudUpload size={large ? 28 : 20} stroke={1.5} />
    </Box>
  );
}

export function SupportedFormatsHint() {
  const { formatsLabel } = useAllowedFileTypes();

  return (
    <Tooltip label={formatsLabel} position="top" withArrow>
      <Text size="xs" lh="18px" fw={FONT_WEIGHTS.medium} c="gray.6" className={classes.formatsHint}>
        Supported formats
      </Text>
    </Tooltip>
  );
}

export function DropStateLabel({ idle }: { idle: string }) {
  return (
    <>
      <Dropzone.Idle>
        <span>{idle}</span>
      </Dropzone.Idle>
      <Dropzone.Accept>
        <span>Drop the files to upload them</span>
      </Dropzone.Accept>
      {/* A dragged file exposes its MIME type but not its name, and the extension is what decides
          on drop, so this state can flag a file that uploads fine. Hence "may", not "is not". */}
      <Dropzone.Reject>
        <span>Some of these files may not be supported</span>
      </Dropzone.Reject>
    </>
  );
}
